import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Tag, Button, Input, Space, message, Select, Modal } from 'antd';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { PlusOutlined, SearchOutlined, EditOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import RecruitmentModal from "../../../../modal/admin/addRecurement";
import EditRecruitmentModal from "../../../../modal/admin/editRecruitmentModal";
import ModalWrapper from '../../../../Modal';
import { getRequest, putRequest, deleteRequest } from "../../../../service/index";
import { formatDate } from "../../../../utils/dateTime";

const { Title } = Typography;
const { Search } = Input;

const Recruitment = () => {
  const [candidates, setCandidates] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isRecruitmentModalOpen, setIsRecruitmentModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const response = await getRequest('/api/onboarding');
      if (response && response.data) {
        // Transform API data to match our component structure
        const transformedCandidates = response.data.map(candidate => ({
          id: candidate._id,
          name: candidate.fullName,
          phone: candidate.phoneNumber,
          date: formatDate(candidate.interviewDate),
          status: candidate.status,
          stage: getStageFromStatus(candidate.status),
          interviewType: candidate.interviewType,
          interviewDate: candidate.interviewDate,
          reference: candidate.reference,
          currentSalary: candidate.currentSalary,
          expectedSalary: candidate.expectedSalary,
          // Include the full API response data for view mode
          fullData: candidate
        }));
        setCandidates(transformedCandidates);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      message.error('Failed to fetch candidates');
    } finally {
      setLoading(false);
    }
  };

  const getStageFromStatus = (status) => {
    switch (status) {
      case 'scheduled':
        return 'interview_schedule';
      case 'first interview':
        return 'interview_conduct';
      case 'second interview':
        return 'second_interview';
      case 'failed':
      case 'success':
        return 'shortlist_results';
      default:
        return 'interview_schedule';
    }
  };


  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    
    // Find the candidate being moved by ID
    const movedCandidate = candidates.find(candidate => candidate.id === result.draggableId);
    
    if (!movedCandidate) return;
    
    // Update the candidate's stage and status
    let newStatus = movedCandidate.status;
    switch (destination.droppableId) {
      case 'interview_schedule':
        newStatus = 'scheduled';
        break;
      case 'interview_conduct':
        newStatus = 'first interview';
        break;
      case 'second_interview':
        newStatus = 'second interview';
        break;
      case 'shortlist_results':
        // Keep the current status if it's failed or success, otherwise don't change status
        // User will select from dropdown
        if (movedCandidate.status !== 'failed' && movedCandidate.status !== 'success') {
          newStatus = movedCandidate.status; // Keep current status, don't change
        }
        break;
      default:
        newStatus = movedCandidate.status;
    }
    
    const updatedCandidate = {
      ...movedCandidate,
      stage: destination.droppableId,
      status: newStatus
    };

    // Update the candidates array immediately for UI responsiveness
    const updatedCandidates = candidates.map(candidate => {
      if (candidate.id === movedCandidate.id) {
        return updatedCandidate;
      }
      return candidate;
    });

    setCandidates(updatedCandidates);

    // Update the status in the backend
    try {
      const response = await putRequest(`/api/onboarding/${movedCandidate.id}`, {
        status: newStatus
      });
      
      if (response) {
        message.success(`Status updated to ${newStatus}`);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('Failed to update status');
      
      // Revert the change if the API call fails
      setCandidates(candidates);
    }
  };

  const getCandidatesByStage = (stage) => {
    return candidates.filter(candidate => candidate.stage === stage);
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'success':
        return 'success-card';
      case 'failed':
        return 'failed-card';
      case 'first interview':
      case 'second interview':
        return 'inprogress-card';
      case 'scheduled':
        return 'scheduled-card';
      default:
        return 'default-card';
    }
  };

  const getTagColor = (status) => {
    switch (status.toLowerCase()) {
      case 'success':
        return 'green';
      case 'failed':
        return 'red';
      case 'first interview':
      case 'second interview':
        return 'blue';
      case 'scheduled':
        return 'cyan';
      default:
        return 'default';
    }
  };

  const handleEdit = (candidate) => {
    // Only allow editing for specific stages
    if (candidate.stage === 'shortlist_results') {
      message.info('Shortlist Results are view-only. Use the dropdown to change status.');
      return;
    }
    setEditingCandidate(candidate);
    setIsEditModalOpen(true);
  };

  const handleView = (candidate) => {
    setEditingCandidate({ ...candidate, isViewMode: true });
    setIsRecruitmentModalOpen(true);
  };

  const handleModalClose = () => {
    setIsRecruitmentModalOpen(false);
    setEditingCandidate(null);
    fetchCandidates(); // Refresh data after modal closes
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setEditingCandidate(null);
    fetchCandidates(); // Refresh data after modal closes
  };

  const handleStatusChange = async (candidateId, newStatus) => {
    try {
      const response = await putRequest(`/api/onboarding/${candidateId}`, {
        status: newStatus
      });
      
      if (response) {
        message.success(`Status updated to ${newStatus}`);
        // Refresh the candidates data
        fetchCandidates();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('Failed to update status');
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleDeleteClick = (candidate) => {
    setCandidateToDelete(candidate);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!candidateToDelete) return;

    try {
      const response = await deleteRequest(`/api/onboarding/${candidateToDelete.id}`);
      if (response) {
        message.success('Candidate deleted successfully');
        setDeleteModalVisible(false);
        setCandidateToDelete(null);
        fetchCandidates(); // Refresh the list
      }
    } catch (error) {
      console.error('Error deleting candidate:', error);
      message.error('Failed to delete candidate');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setCandidateToDelete(null);
  };

  const filteredCandidates = candidates.filter(candidate =>
    candidate.name.toLowerCase().includes(searchText.toLowerCase()) ||
    candidate.phone.includes(searchText)
  );

  const renderCandidateCard = (candidate, index) => (
    <Draggable key={candidate.id} draggableId={candidate.id} index={index}>
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className="mb-2"
        >
          <Card 
            size="small" 
            className={`w-100 ${getStatusColor(candidate.status)}`}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
            <p className='text-white'><strong>Name:</strong> {candidate.name}</p>
            <p className='text-white'><strong>Phone:</strong> {candidate.phone}</p>
            <p className='text-white'><strong>Date:</strong> {candidate.date}</p>
                {candidate.stage === 'shortlist_results' ? (
                  <Select
                    size="small"
                    value={candidate.status}
                    onChange={(value) => handleStatusChange(candidate.id, value)}
                    options={[
                      { value: 'failed', label: 'Failed' },
                      { value: 'success', label: 'Success' }
                    ]}
                    className="w-full mt-1"
                    style={{ minWidth: '80px' }}
                  />
                ) : (
            <Tag color={getTagColor(candidate.status)}>
              {candidate.status}
            </Tag>
                )}
              </div>
              <div className="flex gap-1 ml-2">
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined className="text-white" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleView(candidate);
                  }}
                  className="p-1 min-w-0"
                />
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined className="text-white" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(candidate);
                  }}
                  className="p-1 min-w-0"
                />
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined className="text-white" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(candidate);
                  }}
                  className="p-1 min-w-0"
                />
              </div>
            </div>
          </Card>
        </div>
      )}
    </Draggable>
  );

  return (
    <>
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <Title level={2}>Recruitment Dashboard</Title>
        <Space>
          <Search
            placeholder="Search candidates..."
            allowClear
            onSearch={handleSearch}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: 250 }}
          />
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            className="bg-primary"
            onClick={() => {
              setEditingCandidate(null);
              setIsRecruitmentModalOpen(true);
            }}
          >
            Add New
          </Button>
        </Space>
      </div>
      
      <DragDropContext onDragEnd={onDragEnd}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card 
              title="Interview Schedule" 
              hoverable
              className="h-100"
            >
              <Droppable droppableId="interview_schedule">
                {(provided, snapshot) => (
                  <div 
                    ref={provided.innerRef} 
                    {...provided.droppableProps}
                    className={`min-h-32 ${snapshot.isDraggingOver ? 'bg-blue-50 border-2 border-dashed border-blue-300' : ''}`}
                  >
                    {filteredCandidates
                      .filter(candidate => candidate.stage === 'interview_schedule')
                      .map((candidate, index) => renderCandidateCard(candidate, index))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card 
              title="Interview Conduct" 
              hoverable
              className="h-100"
            >
              <Droppable droppableId="interview_conduct">
                {(provided, snapshot) => (
                  <div 
                    ref={provided.innerRef} 
                    {...provided.droppableProps}
                    className={`min-h-32 ${snapshot.isDraggingOver ? 'bg-blue-50 border-2 border-dashed border-blue-300' : ''}`}
                  >
                    {filteredCandidates
                      .filter(candidate => candidate.stage === 'interview_conduct')
                      .map((candidate, index) => renderCandidateCard(candidate, index))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card 
              title="Second Interview" 
              hoverable
              className="h-100"
            >
              <Droppable droppableId="second_interview">
                {(provided, snapshot) => (
                  <div 
                    ref={provided.innerRef} 
                    {...provided.droppableProps}
                    className={`min-h-32 ${snapshot.isDraggingOver ? 'bg-blue-50 border-2 border-dashed border-blue-300' : ''}`}
                  >
                    {filteredCandidates
                      .filter(candidate => candidate.stage === 'second_interview')
                      .map((candidate, index) => renderCandidateCard(candidate, index))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card 
              title="Shortlist Results" 
              hoverable
              className="h-100"
            >
              <Droppable droppableId="shortlist_results">
                {(provided, snapshot) => (
                  <div 
                    ref={provided.innerRef} 
                    {...provided.droppableProps}
                    className={`min-h-32 ${snapshot.isDraggingOver ? 'bg-blue-50 border-2 border-dashed border-blue-300' : ''}`}
                  >
                    {filteredCandidates
                      .filter(candidate => candidate.stage === 'shortlist_results')
                      .map((candidate, index) => renderCandidateCard(candidate, index))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </Card>
          </Col>
        </Row>
      </DragDropContext>
    </div>

    <ModalWrapper
      open={isRecruitmentModalOpen}
      onRequestClose={handleModalClose}
      title={editingCandidate ? "Edit Recruitment" : "Add Recruitment"}
    >
      <RecruitmentModal 
        open={isRecruitmentModalOpen} 
        onRequestClose={handleModalClose}
        editingData={editingCandidate}
      />
    </ModalWrapper>

    <EditRecruitmentModal
      open={isEditModalOpen}
      onRequestClose={handleEditModalClose}
      editingData={editingCandidate}
    />

    {/* Delete Confirmation Modal */}
    <Modal
      title="Confirm Delete"
      open={deleteModalVisible}
      onOk={handleDeleteConfirm}
      onCancel={handleDeleteCancel}
      okText="Delete"
      cancelText="Cancel"
      okButtonProps={{ danger: true }}
    >
      <p>
        Are you sure you want to delete the candidate <strong>{candidateToDelete?.name}</strong>?
      </p>
      <p>This action cannot be undone.</p>
    </Modal>
    </>
  );
};

export default Recruitment;
